import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { FileText, Plus, Download, Send, Trash2, Search, Bell, X, CheckCircle, Filter, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const InvoicesTab = () => {
  const { token } = useAuth();
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sendingBulkInvoices, setSendingBulkInvoices] = useState(false);

  // Filters
  const [invoiceFilterSearch, setInvoiceFilterSearch] = useState('');
  const [invoiceFilterStatus, setInvoiceFilterStatus] = useState('all');
  const [invoiceSortOrder, setInvoiceSortOrder] = useState('alpha_asc');

  // Create/Edit form
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    client_name: '', client_email: '', client_address: '', issue_date: new Date().toISOString().split('T')[0],
    due_date: '', notes: '', tax_rate: 0, items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }]
  });

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const res = await api.get('/api/invoices');
      setInvoices(res.data || []);
    } catch (error) { toast.error('Error al cargar facturas'); }
    finally { setLoadingInvoices(false); }
  };

  const getFilteredAndSortedInvoices = () => {
    let filtered = [...invoices];
    if (invoiceFilterSearch.trim()) {
      const s = invoiceFilterSearch.toLowerCase().trim();
      filtered = filtered.filter(inv => inv.client_name?.toLowerCase().includes(s) || inv.client_email?.toLowerCase().includes(s) || inv.invoice_number?.toLowerCase().includes(s));
    }
    if (invoiceFilterStatus !== 'all') filtered = filtered.filter(inv => inv.payment_status === invoiceFilterStatus);
    filtered.sort((a, b) => {
      switch (invoiceSortOrder) {
        case 'alpha_asc': return (a.client_name || '').localeCompare(b.client_name || '', 'es');
        case 'alpha_desc': return (b.client_name || '').localeCompare(a.client_name || '', 'es');
        case 'date_asc': return new Date(a.issue_date) - new Date(b.issue_date);
        case 'date_desc': return new Date(b.issue_date) - new Date(a.issue_date);
        default: return 0;
      }
    });
    return filtered;
  };

  const resetInvoiceForm = () => {
    setInvoiceForm({ client_name: '', client_email: '', client_address: '', issue_date: new Date().toISOString().split('T')[0], due_date: '', notes: '', tax_rate: 0, items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }] });
    setEditingInvoiceId(null);
  };

  const openCreateDialog = () => {
    resetInvoiceForm();
    setShowInvoiceDialog(true);
  };

  const generateMonthlyInvoices = async () => {
    const month = window.prompt('¿Para qué mes generar facturas? (formato YYYY-MM)', new Date().toISOString().slice(0, 7));
    if (!month) return;
    try {
      const res = await api.post('/api/invoices/generate-monthly', { billing_month: month });
      toast.success(res.data.message);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar facturas');
    }
  };

  const openEditDialog = (invoice) => {
    setEditingInvoiceId(invoice.id);
    setInvoiceForm({
      client_name: invoice.client_name || '',
      client_email: invoice.client_email || '',
      client_address: invoice.client_address || '',
      issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      tax_rate: invoice.tax_rate || 0,
      items: (invoice.items && invoice.items.length > 0) 
        ? invoice.items.map(item => ({
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            amount: item.amount || 0
          }))
        : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
    setShowInvoiceDialog(true);
  };

  const updateItem = (idx, field, value) => {
    const items = [...invoiceForm.items];
    items[idx][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      items[idx].amount = (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_price) || 0);
    }
    setInvoiceForm({ ...invoiceForm, items });
  };

  const addItem = () => {
    setInvoiceForm({ ...invoiceForm, items: [...invoiceForm.items, { description: '', quantity: 1, unit_price: 0, amount: 0 }] });
  };

  const removeItem = (idx) => {
    if (invoiceForm.items.length <= 1) return;
    const items = invoiceForm.items.filter((_, i) => i !== idx);
    setInvoiceForm({ ...invoiceForm, items });
  };

  const calcSubtotal = () => invoiceForm.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const calcTax = () => calcSubtotal() * (parseFloat(invoiceForm.tax_rate) || 0) / 100;
  const calcTotal = () => calcSubtotal() + calcTax();

  const saveInvoice = async () => {
    if (!invoiceForm.client_name) { toast.error('Nombre del cliente requerido'); return; }
    try {
      const payload = { ...invoiceForm, subtotal: calcSubtotal(), tax_amount: calcTax(), total: calcTotal() };
      if (editingInvoiceId) {
        await api.put(`/api/invoices/${editingInvoiceId}`, payload);
        toast.success('Factura actualizada exitosamente');
      } else {
        await api.post('/api/invoices', payload);
        toast.success('Factura creada exitosamente');
      }
      setShowInvoiceDialog(false);
      resetInvoiceForm();
      loadInvoices();
    } catch (error) { toast.error(editingInvoiceId ? 'Error al actualizar factura' : 'Error al crear factura'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Eliminar esta factura?')) return;
    try {
      await api.delete(`/api/invoices/${id}`);
      toast.success('Factura eliminada');
      loadInvoices();
    } catch (error) { toast.error('Error al eliminar factura'); }
  };

  const markAsPaid = async (id) => {
    try {
      await api.put(`/api/invoices/${id}`, { payment_status: 'paid' });
      toast.success('Factura marcada como pagada');
      loadInvoices();
    } catch (error) { toast.error('Error al actualizar factura'); }
  };

  const sendInvoice = async (id) => {
    try {
      const res = await api.post(`/api/invoices/${id}/send`);
      toast.success(res.data.sent ? 'Factura enviada por email' : 'SMTP no configurado');
    } catch (error) { toast.error('Error al enviar factura'); }
  };

  const sendBulkInvoices = async () => {
    setSendingBulkInvoices(true);
    try {
      await api.post('/api/invoices/send-bulk', { invoice_ids: selectedInvoices });
      toast.success(`${selectedInvoices.length} factura(s) procesadas`);
      setSelectedInvoices([]);
    } catch (error) { toast.error('Error al enviar facturas'); }
    finally { setSendingBulkInvoices(false); }
  };

  const triggerInvoiceReminders = async () => {
    try {
      const res = await api.post('/api/invoices/reminders');
      toast.success(`Recordatorios procesados: ${res.data.reminders_sent} enviados`);
      loadInvoices();
    } catch (error) { toast.error('Error al enviar recordatorios'); }
  };

  const downloadInvoicePdf = async (id, invoiceNumber) => {
    try {
      const res = await api.get(`/api/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factura_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) { toast.error('Error al descargar PDF'); }
  };

  const toggleInvoiceSelection = (id) => {
    setSelectedInvoices(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleAllInvoices = () => {
    const unpaid = invoices.filter(inv => inv.payment_status !== 'paid').map(inv => inv.id);
    setSelectedInvoices(prev => prev.length === unpaid.length ? [] : unpaid);
  };

  const filtered = getFilteredAndSortedInvoices();

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" /> Facturas</CardTitle>
              <CardDescription>Gestionar facturas de clientes</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedInvoices.length > 0 && (
                <Button onClick={sendBulkInvoices} disabled={sendingBulkInvoices} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="send-bulk-invoices-btn">
                  <Send className="h-4 w-4 mr-2" /> Enviar {selectedInvoices.length} Factura(s)
                </Button>
              )}
              <Button variant="outline" onClick={triggerInvoiceReminders} data-testid="send-reminders-btn">
                <Bell className="h-4 w-4 mr-2" /> Recordatorios
              </Button>
              <Button variant="outline" onClick={generateMonthlyInvoices} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="generate-monthly-invoices-btn">
                <FileText className="h-4 w-4 mr-2" /> Generar mes
              </Button>
              <Button onClick={openCreateDialog} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="new-invoice-btn">
                <Plus className="h-4 w-4 mr-2" /> Nueva Factura
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-slate-50/50">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Buscar por cliente, email o # factura..." value={invoiceFilterSearch} onChange={(e) => setInvoiceFilterSearch(e.target.value)} className="pl-10 bg-white" data-testid="invoice-search-input" />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <select value={invoiceFilterStatus} onChange={(e) => setInvoiceFilterStatus(e.target.value)} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="invoice-status-filter">
                <option value="all">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="paid">Pagada</option>
                <option value="overdue">Vencida</option>
                <option value="partial">Parcial</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Ordenar:</span>
              <select value={invoiceSortOrder} onChange={(e) => setInvoiceSortOrder(e.target.value)} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" data-testid="invoice-sort-order">
                <option value="alpha_asc">Cliente A-Z</option>
                <option value="alpha_desc">Cliente Z-A</option>
                <option value="date_desc">Fecha (reciente)</option>
                <option value="date_asc">Fecha (antigua)</option>
              </select>
            </div>
            {(invoiceFilterSearch || invoiceFilterStatus !== 'all' || invoiceSortOrder !== 'alpha_asc') && (
              <Button variant="ghost" size="sm" onClick={() => { setInvoiceFilterSearch(''); setInvoiceFilterStatus('all'); setInvoiceSortOrder('alpha_asc'); }} className="text-slate-500 hover:text-slate-700" data-testid="invoice-clear-filters">
                <X className="h-4 w-4 mr-1" /> Limpiar
              </Button>
            )}
          </div>
          <div className="mt-2 text-sm text-slate-500">Mostrando {filtered.length} de {invoices.length} facturas</div>
        </div>

        <CardContent>
          {loadingInvoices ? (
            <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div><p className="text-slate-500 mt-2">Cargando facturas...</p></div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg"><FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 mb-2">No hay facturas creadas</p><p className="text-sm text-slate-400">Crea tu primera factura para comenzar</p></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg"><Search className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 mb-2">No se encontraron facturas</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setInvoiceFilterSearch(''); setInvoiceFilterStatus('all'); setInvoiceSortOrder('alpha_asc'); }}>Limpiar filtros</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="invoices-table">
                <thead><tr className="border-b bg-slate-50">
                  <th className="py-3 px-4 w-10"><input type="checkbox" checked={selectedInvoices.length > 0 && selectedInvoices.length === invoices.filter(inv => inv.payment_status !== 'paid').length} onChange={toggleAllInvoices} className="h-4 w-4 rounded border-slate-300 text-orange-600" /></th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Factura #</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Cliente</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Fecha</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Vencimiento</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Total</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                </tr></thead>
                <tbody>
                  {filtered.map((invoice) => (
                    <tr key={invoice.id} className={`border-b hover:bg-slate-50 ${selectedInvoices.includes(invoice.id) ? 'bg-orange-50' : ''}`}>
                      <td className="py-3 px-4"><input type="checkbox" checked={selectedInvoices.includes(invoice.id)} onChange={() => toggleInvoiceSelection(invoice.id)} disabled={invoice.payment_status === 'paid'} className="h-4 w-4 rounded border-slate-300 text-orange-600 disabled:opacity-50" /></td>
                      <td className="py-3 px-4"><span className="font-mono text-sm font-medium text-indigo-600">{invoice.invoice_number}</span></td>
                      <td className="py-3 px-4"><p className="font-medium text-slate-800">{invoice.client_name}</p><p className="text-sm text-slate-500">{invoice.client_email}</p></td>
                      <td className="py-3 px-4 text-sm text-slate-600">{invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('es-ES') : '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('es-ES') : '-'}</td>
                      <td className="py-3 px-4"><span className="font-semibold text-slate-800">${(invoice.total || 0).toFixed(2)}</span></td>
                      <td className="py-3 px-4">
                        <Badge className={
                          invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                          invoice.payment_status === 'overdue' ? 'bg-red-100 text-red-700' :
                          invoice.payment_status === 'partial' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {invoice.payment_status === 'paid' ? 'Pagada' : invoice.payment_status === 'overdue' ? 'Vencida' : invoice.payment_status === 'partial' ? 'Parcial' : 'Pendiente'}
                        </Badge>
                        {(invoice.reminder_7d_sent || invoice.reminder_3d_sent) && <span className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Bell className="h-3 w-3" />{invoice.reminder_3d_sent ? '3d' : '7d'}</span>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number)} title="Descargar PDF" data-testid={`download-invoice-${invoice.id}`}><Download className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(invoice)} title="Editar Factura" className="text-blue-600 hover:text-blue-700" data-testid={`edit-invoice-${invoice.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => sendInvoice(invoice.id)} title="Enviar por Email" disabled={invoice.payment_status === 'paid'} data-testid={`send-invoice-${invoice.id}`}><Send className="h-4 w-4" /></Button>
                          {invoice.payment_status !== 'paid' && <Button variant="outline" size="sm" onClick={() => markAsPaid(invoice.id)} title="Marcar como Pagada" className="text-green-600 hover:text-green-700" data-testid={`mark-paid-${invoice.id}`}><CheckCircle className="h-4 w-4" /></Button>}
                          <Button variant="outline" size="sm" onClick={() => deleteInvoice(invoice.id)} title="Eliminar" className="text-red-600 hover:text-red-700" data-testid={`delete-invoice-${invoice.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{editingInvoiceId ? 'Editar Factura' : 'Nueva Factura'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nombre del Cliente *</Label><Input value={invoiceForm.client_name} onChange={(e) => setInvoiceForm({...invoiceForm, client_name: e.target.value})} data-testid="invoice-client-name" /></div>
              <div><Label>Email del Cliente</Label><Input type="email" value={invoiceForm.client_email} onChange={(e) => setInvoiceForm({...invoiceForm, client_email: e.target.value})} data-testid="invoice-client-email" /></div>
            </div>
            <div><Label>Direccion del Cliente</Label><Input value={invoiceForm.client_address} onChange={(e) => setInvoiceForm({...invoiceForm, client_address: e.target.value})} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Fecha de Emision</Label><Input type="date" value={invoiceForm.issue_date} onChange={(e) => setInvoiceForm({...invoiceForm, issue_date: e.target.value})} /></div>
              <div><Label>Fecha de Vencimiento</Label><Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})} /></div>
              <div><Label>Impuesto %</Label><Input type="number" step="0.01" value={invoiceForm.tax_rate} onChange={(e) => setInvoiceForm({...invoiceForm, tax_rate: parseFloat(e.target.value) || 0})} /></div>
            </div>

            {/* Items */}
            <div>
              <Label className="mb-2 block">Items</Label>
              {invoiceForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 mb-2 items-end">
                  <div className="flex-1"><Input placeholder="Descripcion" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} /></div>
                  <div className="w-20"><Input type="number" placeholder="Cant." value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} /></div>
                  <div className="w-28"><Input type="number" step="0.01" placeholder="Precio" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} /></div>
                  <div className="w-28 text-right font-mono pt-2">${(parseFloat(item.amount) || 0).toFixed(2)}</div>
                  {invoiceForm.items.length > 1 && <Button size="sm" variant="ghost" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addItem} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Agregar Item</Button>
            </div>

            {/* Totals */}
            <div className="bg-slate-50 rounded-lg p-4 text-right space-y-1">
              <p>Subtotal: <span className="font-mono font-medium">${calcSubtotal().toFixed(2)}</span></p>
              <p>Impuesto ({invoiceForm.tax_rate}%): <span className="font-mono font-medium">${calcTax().toFixed(2)}</span></p>
              <p className="text-lg font-bold text-orange-600">Total: <span className="font-mono">${calcTotal().toFixed(2)}</span></p>
            </div>

            <div><Label>Notas</Label><Textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancelar</Button>
            <Button onClick={saveInvoice} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="save-invoice-btn">
              {editingInvoiceId ? 'Guardar Cambios' : 'Crear Factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesTab;
